import { Component, Input, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxDataGridModule, DxDataGridComponent, DxLoadPanelModule, DxButtonModule } from 'devextreme-angular';
import { CrudService } from '../services/crud.service';
import { environment } from 'src/environments/environment';
import CustomStore from 'devextreme/data/custom_store';
import { MediaTypes, mediaTypeDataField, findMediaTypeForKey } from '../models/media-types.model';
import notify from 'devextreme/ui/notify';
import { lastValueFrom, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { GridProfileToolbarComponent } from '../grid-profile-toolbar/grid-profile-toolbar.component';
import { PageHeaderComponent } from 'src/app/shared/page-header/page-header.component';

interface MediaTypeColumn {
  id: number;
  dataField: string;
  caption: string;
  dataType: string;
  calculateCellValue?: (rowData: any) => any;
  setCellValue?: (newData: any, value: any) => void;
}

@Component({
  selector: 'app-promotion-group-products',
  standalone: true,
  imports: [CommonModule, DxDataGridModule, DxLoadPanelModule, DxButtonModule, GridProfileToolbarComponent, PageHeaderComponent],
  templateUrl: './promotion-group-products.component.html',
  styleUrl: './promotion-group-products.component.css'
})
export class PromotionGroupProductsComponent implements OnInit, AfterViewInit {
  @Input() promotionGroupId: number;
  @Input() buyingGroupId: number;
  @Input() promotionPeriodId: number;
  @ViewChild('groupProductsGrid', { static: false }) dataGrid: DxDataGridComponent;
  @ViewChild('profileToolbar') profileToolbar: GridProfileToolbarComponent;

  productDataSource: CustomStore;
  products: any[] = [];
  mediaTypes: MediaTypes[] = [];
  mediaTypeColumns: MediaTypeColumn[] = [];
  loading = true;
  isBuilt = false;
  promoInfo = '';

  private mediaTypesLoaded = false;
  private productsLoaded = false;
  private viewInitialized = false;
  private readonly apiUrl = environment.baseApiUrl;

  constructor(
    private crudService: CrudService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.initDataSource();
    this.loadMediaTypes();
  }

  ngOnInit() {
    this.loadPromoInfo();
    this.subscribeToQueryParams();
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.checkLoadComplete();
  }

  loadState = () => null;

  saveState = (state: any) => {
    if (this.isBuilt) {
      this.profileToolbar?.saveCurrentState(JSON.stringify(state));
    }
  }

  onProfileLoaded(gridState: string | null) {
    if (!this.dataGrid?.instance) return;
    if (gridState !== null) {
      try {
        this.dataGrid.instance.state(JSON.parse(gridState));
      } catch (e) {
        console.error('Failed to parse grid profile state:', e);
        notify({ message: 'Failed to load profile layout', width: 300 }, 'error', 3000);
      }
    } else {
      this.dataGrid.instance.state(null);
    }
  }

  returnToPlanning() {
    this.router.navigate(['/promotions']);
  }

  getColumnDataType(fieldType: string): string {
    switch (fieldType) {
      case 'Text': return 'string';
      case 'Number': return 'number';
      case 'Checkbox': return 'boolean';
      default: return 'string';
    }
  }

  private loadPromoInfo() {
    const selectedPromo = localStorage.getItem('selectedPromo');
    if (selectedPromo && !this.promotionGroupId) {
      const header = JSON.parse(selectedPromo);
      this.promoInfo = `${header.promotion_name} Details: ${header.promotion_start} - ${header.promotion_end}`;
    }
  }

  private subscribeToQueryParams() {
    this.route.queryParams.subscribe(params => {
      if (params['promotion_period_id']) {
        this.promotionPeriodId = Number(params['promotion_period_id']);
      }
      if (params['buying_group_id']) {
        this.buyingGroupId = Number(params['buying_group_id']);
      }
    });
  }

  private initDataSource() {
    this.productDataSource = new CustomStore({
      // product_code is no longer unique per location (identity is code + liquorfile_number)
      // — key rows by the product row id instead.
      key: 'product_id',
      load: async () => {
        try {
          this.loadIdsFromStorage();
          const url = `${this.apiUrl}media-planning/1?buying_group_id=${this.buyingGroupId}&promotion_period_id=${this.promotionPeriodId}`;
          const productData = await this.crudService.getData(url).toPromise();
          this.products = productData.data || productData || [];
          return this.products;
        } catch (error) {
          console.error('Error loading products:', error);
          return [];
        } finally {
          this.productsLoaded = true;
          this.checkLoadComplete();
        }
      },
      update: async (key, values: any) => {
        const rowIndex = this.dataGrid.instance.getRowIndexByKey(key);
        this.dataGrid.instance.selectRowsByIndexes([rowIndex]);
        const rowData = this.dataGrid.instance.getSelectedRowsData()[0];

        const updates = Object.keys(values)
          .map(currentKey => {
            const mediaType = findMediaTypeForKey(this.mediaTypes, currentKey);
            if (!mediaType) {
              console.warn(`No media type found for values key '${currentKey}' — skipping`);
              return null;
            }
            return {
              media_type_id: mediaType.id,
              media_plan_description: (values[currentKey] !== '' && values[currentKey] !== null) ? values[currentKey] : 'API_DELETE',
              promotion_header_id: rowData.promotion_header_id,
              promotion_group_id: rowData.promotion_group_id,
              linked_promotion_period_id: rowData.linked_promotion_period_id || 0,
              product_id: rowData.product_id
            };
          })
          .filter(Boolean);

        if (updates.length > 0) {
          await lastValueFrom(
            this.crudService.insertData(`${this.apiUrl}media-planning/bulk-update`, { updates }).pipe(
              tap((result: any) => {
                notify({ message: result.data }, result.success ? 'success' : 'error');
              })
            )
          );
        }
        return values;
      }
    });
  }

  private loadIdsFromStorage() {
    if (!this.buyingGroupId || !this.promotionPeriodId) {
      const header = JSON.parse(localStorage.getItem('selectedPromo') || '{}');
      this.buyingGroupId = this.buyingGroupId || header.buying_group_id;
      this.promotionPeriodId = this.promotionPeriodId || header.promotion_period_id;
    }
  }

  private async loadMediaTypes() {
    try {
      this.mediaTypes = await lastValueFrom(this.crudService.getData(`${this.apiUrl}media-types`)) as MediaTypes[];
      this.buildMediaTypeColumns();
    } catch (error) {
      console.error('Error loading media types:', error);
    } finally {
      this.mediaTypesLoaded = true;
      this.checkLoadComplete();
    }
  }

  // Built once when media types load — the calculator/setter closures must stay
  // referentially stable or re-created columns reset the grid's stored state.
  private buildMediaTypeColumns() {
    this.mediaTypeColumns = this.mediaTypes.map(mediaType => {
      const column: MediaTypeColumn = {
        id: mediaType.id,
        dataField: mediaTypeDataField(mediaType),
        caption: mediaType.media_type_name,
        dataType: this.getColumnDataType(mediaType.media_type_field_type)
      };
      if (mediaType.media_type_name.includes('.')) {
        const name = mediaType.media_type_name;
        const sanitised = column.dataField;
        // Sanitised key first: DevExtreme merges pending batch edits and
        // reshape-mode save results under it; the dotted key only holds the
        // initially loaded value.
        column.calculateCellValue = (rowData: any) => rowData[sanitised] !== undefined ? rowData[sanitised] : rowData[name];
        column.setCellValue = (newData: any, value: any) => { newData[sanitised] = value; };
      }
      return column;
    });
  }

  onEditorPreparing(e: any) {
    if(e.parentType === "dataRow")
     e.editorOptions.onFocusIn = function (args: any) {
       args.element.querySelector("input.dx-texteditor-input").select();
     };
  }

  private checkLoadComplete() {
    if (this.mediaTypesLoaded && this.productsLoaded && this.viewInitialized) {
      this.isBuilt = true;
      this.loading = false;
    }
  }
}
